<template>
  <div class="page__auth-callback">
    <div v-if="isLoading" class="text-spinner">
      <b-spinner variant="primary" style="width: 5rem; height: 5rem" />
    </div>
    <ModalPolicy
      v-else
      :show="step === 'PDPA'"
      cancelable
      @completed="onPdpaComplete"
      @canceled="onPdpaCanceled"
    />
  </div>
</template>

<script>
import { userInfoMixin } from '@/mixins/userInfoMixin';
import { optimizelyMixin } from '@/mixins/optimizelyMixin';
import { useAuth } from '@/composables';

const { googleAuthen, logout } = useAuth();

import { CatalogConsent } from '@/models/catalog/CatalogConsent';
import UserJobStatus from '@/models/UserJobStatus';
import ModalPolicy from '@/views/PDPA/components/ModalPolicy.vue';
export default {
  name: 'PageAuthCallback',
  components: {
    ModalPolicy
  },

  mixins: [userInfoMixin, optimizelyMixin],

  data() {
    return {
      isLoading: false,
      authCode: '',
      isGoogleAuthSuccess: null,
      data: null,
      step: parseInt(this.$route.query.step) || 'SIGN_UP',
      pdpaPayload: null
    };
  },

  created() {
    this.authCode = this.$route.query.code;
    this.authenticateGoogleUser();
  },

  methods: {
    async authenticateGoogleUser() {
      this.isLoading = true;
      try {
        this.isGoogleAuthSuccess = await googleAuthen(this.authCode);

        // Check if the user exists and Google authentication is successful
        if (this.user && this.isGoogleAuthSuccess) {
          this.handleGoogleAuthSuccessful();
        } else {
          this.handleGoogleAuthError();
        }
      } catch (error) {
        this.$toast.error('Something went wrong.');
      } finally {
        this.isLoading = false;
      }
    },

    async handleGoogleAuthSuccessful() {
      this.isLoading = true;

      let isUserConsentInvalid = await this.validateUserConsent();

      if (isUserConsentInvalid) {
        this.step = 'PDPA';
      } else {
        await this.checkOnboardingAndRedirect();
      }

      this.isLoading = false;
    },

    async handleGoogleAuthError() {
      this.isLoading = true;
      // check if Google authentication is failed
      const error = this.$route.query.error;

      if (error) {
        // if Google authentication is failed and error is DUPLICATE_EMAIL, redirect to sign in page with error message
        if (error === 'DUPLICATE_EMAIL' || error === 'MISSING_LASTNAME') {
          this.$router.push({ name: 'SignIn', query: { error: error } });
        }
      } else {
        this.$toast.error('Something went wrong.');
        this.$router.push({ name: 'SignIn' });
      }
      this.isLoading = false;
    },

    async getUserOnboardingRecord() {
      try {
        const {
          response: { data }
        } = await UserJobStatus.api().find();

        if (data && data.length) {
          const recordOnBoarding = data.find((item) => item.user?.id === this.userId);

          return !!recordOnBoarding;
        }

        return false;
      } catch (error) {
        console.error('Error fetching user onboarding record:', error);
        return false;
      }
    },

    async getConsentDocuments() {
      try {
        const response = await CatalogConsent.api().requestConsents();
        const data = response?.response?.data?.data;
        
        const hasConsented = data?.consents ? data.consents.length === 0 : true;
        
        console.log('[App.vue] checked user consent status: ', hasConsented);

        return hasConsented;
      } catch (error) {
        console.error('[App.vue] Failed to check user consent: ', error);
        return false; // Force re-consent on error
      }
    },

    async validateUserConsent() {
      let invalid = false;

      if (this.user) {
        const hasConsented = await this.getConsentDocuments();
        // invalid is true if they have NOT consented
        invalid = !hasConsented;
      }

      return invalid;
    },

    async onPdpaComplete(payload) {
      this.pdpaPayload = payload;

      await this.confirm();
    },

    async onPdpaCanceled() {
      await logout();
    },

    async confirm() {
      this.isLoading = true;
      try {
        this.loadingMessage = 'Registering user, please wait...';

        if (!this.user) {
          throw new Error('User not found');
        }

        await CatalogConsent.api().submitConsents(this.pdpaPayload);

        await this.checkOnboardingAndRedirect();
      } catch (error) {
        console.error('Error in confirm:', error);
        this.notiErrorDefault('Something went wrong.');
      } finally {
        this.isLoading = false;
      }
    },

    async checkOnboardingAndRedirect() {
      const isUserOnboarded = await this.getUserOnboardingRecord();

      // Redirect to onboarding if user is not onboarded and onboarding feature is enabled
      if (!this.user.is_onboarded && !isUserOnboarded && this.$_isFeatureEnabled('onboarding')) {
        this.$router.push({ name: 'on-boarding' });
      } else {
        await this.redirectToPreviousOrHome();
      }
    },

    async redirectToPreviousOrHome() {
      const previousPath = localStorage.getItem('previousPath');

      if (previousPath) {
        // Clear the stored path and redirect to it
        localStorage.removeItem('previousPath');
        this.$router.push({ path: previousPath });
      } else {
        // Default to learning-unit page
        this.$router.push({ name: 'learning-unit' });
      }
    }
  }
};
</script>

<style scoped>
.page__auth-callback {
  margin: -1.5rem -1.25rem;
}
.text-spinner {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
